import { FeatureFlag } from '@/types/featureFlag';
import { ReturnComponent } from '@/utils/reactTypes';
import { useFeatureFlagsEnabledQuery } from '@/utils/useFeatureFlagEnabled';
import { useQuery } from '@apollo/client/react';

import { PATIENT_CARE_TEAM_QUERY } from './PatientCareTeam.queries';
import { CareTeamInfoSkeleton, PatientCareTeamInfo } from './PatientCareTeamInfo';

const CARE_TEAM_FEATURE_FLAGS = {
    [FeatureFlag.CARE_TEAM_EFFECTIVE_DATES]: FeatureFlag.CARE_TEAM_EFFECTIVE_DATES,
    [FeatureFlag.DASHBOARD_CARE_TEAM_RECOMMENDATION_FLOW]:
        FeatureFlag.DASHBOARD_CARE_TEAM_RECOMMENDATION_FLOW,
    [FeatureFlag.DASHBOARD_SHOW_CARE_TEAM_MEMBER_ATTRIBUTE_CHIPS]:
        FeatureFlag.DASHBOARD_SHOW_CARE_TEAM_MEMBER_ATTRIBUTE_CHIPS,
};

// Owns the care team's data and flags so every surface shows the same section and the same
// placeholder while it loads. Non-suspending, since these rails sit outside any boundary.
export const PatientCareTeam = ({
    patientId,
    showDisciplineCodes = false,
}: {
    patientId: string;
    showDisciplineCodes?: boolean;
}): ReturnComponent => {
    const { data, loading } = useQuery(PATIENT_CARE_TEAM_QUERY, {
        variables: { patientId },
        fetchPolicy: 'cache-and-network',
    });
    const { enabled: flags, loading: flagsLoading } =
        useFeatureFlagsEnabledQuery(CARE_TEAM_FEATURE_FLAGS);

    const patient = data?.patient;

    if (!patient || flagsLoading) {
        return loading || flagsLoading ? <CareTeamInfoSkeleton /> : null;
    }

    return (
        <PatientCareTeamInfo
            patient={patient}
            showDisciplineCodes={showDisciplineCodes}
            hasRecommendationFlow={flags[FeatureFlag.DASHBOARD_CARE_TEAM_RECOMMENDATION_FLOW]}
            hasEffectiveDates={flags[FeatureFlag.CARE_TEAM_EFFECTIVE_DATES]}
            showAttributeChips={flags[FeatureFlag.DASHBOARD_SHOW_CARE_TEAM_MEMBER_ATTRIBUTE_CHIPS]}
        />
    );
};
